import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  Camera, Upload, Sparkles, Trash2, Wand2, Loader2, ImageIcon,
  CheckCircle2, AlertCircle, ArrowLeftRight, Plus, Minus, Move,
  Brush, Lightbulb, History as HistoryIcon
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_ICONS = {
  remove: Minus,
  add: Plus,
  organize: Sparkles,
  move: Move,
  clean: Brush,
  light: Lightbulb,
};

const ACTION_COLORS = {
  remove: '#FFB3BA',
  add: '#B9FBC0',
  organize: '#C8B6FF',
  move: '#FFE8A3',
  clean: '#A2D2FF',
  light: '#FFD6A5',
};

const PRIORITY_LABEL = { high: 'Hög', medium: 'Medel', low: 'Låg' };
const PRIORITY_COLOR = { high: '#FF8FA3', medium: '#FFD166', low: '#B9FBC0' };

/** Compress image to keep base64 under ~1.5MB and max edge ~1600px */
async function fileToCompressedBase64(file, maxEdge = 1600, quality = 0.82) {
  const dataUrl = await new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise((resolve, reject) => {
    const el = new Image();
    el.onload = () => resolve(el);
    el.onerror = reject;
    el.src = dataUrl;
  });
  const scale = Math.min(1, maxEdge / Math.max(img.width, img.height));
  const w = Math.round(img.width * scale);
  const h = Math.round(img.height * scale);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d');
  ctx.drawImage(img, 0, 0, w, h);
  return canvas.toDataURL('image/jpeg', quality);
}

function BeforeAfterSlider({ before, after }) {
  const [pos, setPos] = useState(50);
  const wrapRef = useRef(null);
  const dragging = useRef(false);

  const updateFromClient = (clientX) => {
    const rect = wrapRef.current.getBoundingClientRect();
    const pct = Math.max(0, Math.min(100, ((clientX - rect.left) / rect.width) * 100));
    setPos(pct);
  };

  const onDown = (e) => {
    dragging.current = true;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClient(x);
  };
  const onMove = (e) => {
    if (!dragging.current) return;
    const x = e.touches ? e.touches[0].clientX : e.clientX;
    updateFromClient(x);
  };
  const onUp = () => { dragging.current = false; };

  useEffect(() => {
    window.addEventListener('mouseup', onUp);
    window.addEventListener('touchend', onUp);
    return () => {
      window.removeEventListener('mouseup', onUp);
      window.removeEventListener('touchend', onUp);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      className="relative w-full aspect-[4/3] overflow-hidden border-[3px] border-black rounded-2xl select-none cursor-ew-resize bg-black"
      onMouseDown={onDown}
      onMouseMove={onMove}
      onTouchStart={onDown}
      onTouchMove={onMove}
      data-testid="room-before-after-slider"
    >
      {/* After (bottom layer, full) */}
      <img src={after} alt="efter" className="absolute inset-0 w-full h-full object-cover pointer-events-none" />
      {/* Before (clipped from left) */}
      <div
        className="absolute inset-0 overflow-hidden pointer-events-none"
        style={{ width: `${pos}%` }}
      >
        <img
          src={before}
          alt="före"
          className="absolute inset-0 h-full object-cover"
          style={{ width: `${100 / (pos / 100 || 0.0001)}%`, maxWidth: 'none' }}
        />
      </div>
      {/* Handle */}
      <div
        className="absolute top-0 bottom-0 w-1 bg-white shadow-[0_0_0_2px_rgba(0,0,0,0.9)] pointer-events-none"
        style={{ left: `${pos}%`, transform: 'translateX(-50%)' }}
      >
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 bg-white border-[3px] border-black rounded-full w-10 h-10 flex items-center justify-center shadow-[3px_3px_0_#000]">
          <ArrowLeftRight className="w-4 h-4" />
        </div>
      </div>
      {/* Labels */}
      <div className="absolute top-3 left-3 bg-white border-[2px] border-black rounded-full px-3 py-1 text-xs font-bold pointer-events-none">FÖRE</div>
      <div className="absolute top-3 right-3 bg-[#B9FBC0] border-[2px] border-black rounded-full px-3 py-1 text-xs font-bold pointer-events-none">EFTER ✨</div>
    </div>
  );
}

function SuggestionCard({ s, done, onToggle, busy }) {
  const Icon = ACTION_ICONS[s.action] || Sparkles;
  const color = ACTION_COLORS[s.action] || '#FFC8DD';
  const pcolor = PRIORITY_COLOR[s.priority] || '#eee';
  return (
    <div
      className={`neo-card p-3 flex gap-3 items-start transition ${done ? 'opacity-60' : ''}`}
      style={{ background: color }}
      data-testid="room-suggestion-card"
    >
      <button
        onClick={onToggle}
        disabled={busy}
        className={`w-10 h-10 shrink-0 rounded-xl border-[3px] border-black flex items-center justify-center text-xl transition shadow-[2px_2px_0_#000] active:translate-x-[1px] active:translate-y-[1px] active:shadow-none ${done ? 'bg-[#B9FBC0]' : 'bg-white'}`}
        data-testid="room-suggestion-toggle"
        aria-label={done ? 'Markera som inte klar' : 'Markera som klar'}
      >
        {done ? <CheckCircle2 className="w-5 h-5" /> : <span aria-hidden>{s.emoji || <Icon className="w-5 h-5" />}</span>}
      </button>
      <div className="flex-1 min-w-0">
        <div className="flex items-center justify-between gap-2 mb-1">
          <h4 className={`font-bold text-sm leading-tight ${done ? 'line-through' : ''}`}>{s.title}</h4>
          <span
            className="text-[10px] font-bold px-2 py-0.5 rounded-full border-[2px] border-black shrink-0"
            style={{ background: pcolor }}
          >
            {PRIORITY_LABEL[s.priority] || 'Medel'}
          </span>
        </div>
        <p className={`text-xs text-black/80 leading-snug ${done ? 'line-through' : ''}`}>{s.description}</p>
      </div>
    </div>
  );
}

function StorageList({ roomId, initialItems, hasAfterImage }) {
  const [items, setItems] = useState(initialItems || []);
  const [loading, setLoading] = useState(false);
  const [savedIds, setSavedIds] = useState(new Set());

  const fetchItems = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/rooms/${roomId}/storage`, {}, { withCredentials: true }
      );
      setItems(data.items || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte hitta förvaringen');
    } finally {
      setLoading(false);
    }
  };

  const saveToWishlist = async (item) => {
    try {
      const { data } = await axios.post(
        `${API}/rooms/${roomId}/storage/save-to-wishlist`,
        { item_id: item.id }, { withCredentials: true }
      );
      setSavedIds((prev) => new Set([...prev, item.id]));
      if (data.already_saved) {
        toast('Redan i din önskelista 💛');
      } else {
        toast.success(`💖 "${item.name}" sparad i Garderoben → Hemsaker`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Kunde inte spara');
    }
  };

  if (!hasAfterImage) return null;

  return (
    <div className="neo-card p-4 bg-white" data-testid="room-storage-section">
      <div className="flex items-center justify-between gap-2 mb-3">
        <h3 className="font-bold text-sm flex items-center gap-1.5">
          <span className="text-lg">🛍️</span> Förvaring i bilden
        </h3>
        {items.length === 0 && (
          <button
            onClick={fetchItems}
            disabled={loading}
            className="neo-btn px-3 py-1.5 bg-[#FFC8DD] font-bold text-xs flex items-center gap-1 disabled:opacity-60"
            data-testid="room-storage-fetch"
          >
            {loading ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Hittar...</> : <><Sparkles className="w-3.5 h-3.5" /> Visa förvaring</>}
          </button>
        )}
      </div>

      {items.length === 0 && !loading && (
        <p className="text-xs opacity-70 italic">
          Klicka knappen ovan så letar AI:n upp förvaringsprodukterna i din efterbild och föreslår var du kan köpa dem 🛒
        </p>
      )}

      {items.length > 0 && (
        <ul className="space-y-2.5" data-testid="room-storage-list">
          {items.map((item, i) => (
            <li key={item.id} className="border-2 border-black rounded-xl p-2.5 bg-[#FDF0D5]/40" data-testid={`room-storage-item-${i}`}>
              <div className="flex items-start gap-2">
                <span className="text-2xl shrink-0 mt-0.5">{item.emoji}</span>
                <div className="flex-1 min-w-0">
                  <div className="flex items-baseline gap-2">
                    <span className="font-bold text-xs opacity-60">{i + 1}.</span>
                    <h4 className="font-bold text-sm leading-tight">{item.name}</h4>
                  </div>
                  {item.detail && (
                    <p className="text-[11px] opacity-75 leading-snug mt-0.5">{item.detail}</p>
                  )}
                  {item.search_query && (
                    <p className="text-[10px] opacity-50 mt-1 font-mono">🔍 {item.search_query}</p>
                  )}
                </div>
              </div>

              {/* Store buttons */}
              <div className="flex flex-wrap gap-1 mt-2">
                {(item.links || []).map((link) => (
                  <a
                    key={link.key}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="neo-btn px-2 py-1 bg-white text-[10px] font-bold flex items-center gap-1 hover:bg-[#FFE8F5]"
                    data-testid={`room-storage-link-${link.key}-${i}`}
                  >
                    <span>{link.emoji}</span>{link.name}
                  </a>
                ))}
                <button
                  onClick={() => saveToWishlist(item)}
                  disabled={savedIds.has(item.id)}
                  className={`neo-btn px-2 py-1 text-[10px] font-bold flex items-center gap-1 ml-auto ${
                    savedIds.has(item.id) ? 'bg-[#B9FBC0]' : 'bg-[#FFC8DD]'
                  }`}
                  data-testid={`room-storage-save-${i}`}
                >
                  {savedIds.has(item.id) ? '💖 Sparad' : '💖 Spara i Garderob'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}


function AnalysisView({ room, onVisualize, visualizing, onDelete, onToggleSuggestion, togglingIdx }) {
  const a = room.analysis || {};
  const sugg = Array.isArray(a.suggestions) ? a.suggestions : [];
  const zones = Array.isArray(a.zones) ? a.zones : [];
  const layoutTips = Array.isArray(a.layout_tips) ? a.layout_tips : [];
  const aestheticTouches = Array.isArray(a.aesthetic_touches) ? a.aesthetic_touches : [];
  const score = Number(a.vibe_score) || 0;
  const scoreColor = score >= 8 ? '#B9FBC0' : score >= 5 ? '#FFE8A3' : '#FFB3BA';
  const completed = Array.isArray(room.completed_suggestions) ? room.completed_suggestions : [];
  const completedSet = new Set(completed);
  const total = sugg.length;
  const doneCount = completedSet.size;
  const pct = total ? Math.round((doneCount / total) * 100) : 0;

  return (
    <div className="space-y-4">
      {/* Images / slider */}
      {room.visualized_image ? (
        <BeforeAfterSlider
          before={`data:image/jpeg;base64,${room.original_image}`}
          after={`data:image/jpeg;base64,${room.visualized_image}`}
        />
      ) : (
        <div className="relative">
          <img
            src={`data:image/jpeg;base64,${room.original_image}`}
            alt={room.title}
            className="w-full aspect-[4/3] object-cover border-[3px] border-black rounded-2xl"
          />
          <div className="absolute top-3 left-3 bg-white border-[2px] border-black rounded-full px-3 py-1 text-xs font-bold">ORIGINAL</div>
        </div>
      )}

      {/* Visualize button */}
      {!room.visualized_image && (
        <button
          onClick={onVisualize}
          disabled={visualizing}
          className="neo-btn w-full py-3 bg-gradient-to-br from-[#C8B6FF] to-[#A2D2FF] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
          data-testid="room-visualize-btn"
        >
          {visualizing ? (
            <>
              <Loader2 className="w-5 h-5 animate-spin" />
              Skapar visualisering... (ca 15-25 sek)
            </>
          ) : (
            <>
              <Wand2 className="w-5 h-5" />
              Visa hur det SKULLE se ut ✨
            </>
          )}
        </button>
      )}

      {/* Storage products from after-image */}
      {room.visualized_image && (
        <StorageList
          roomId={room.id}
          initialItems={room.storage_items || []}
          hasAfterImage={true}
        />
      )}

      {/* Summary + score */}
      <div className="neo-card p-4 bg-white" data-testid="room-summary-card">
        <div className="flex items-start gap-3 mb-2">
          <div
            className="w-14 h-14 rounded-2xl border-[3px] border-black flex flex-col items-center justify-center shrink-0 shadow-[3px_3px_0_#000]"
            style={{ background: scoreColor }}
          >
            <span className="text-lg font-black leading-none">{score}</span>
            <span className="text-[9px] font-bold">/10</span>
          </div>
          <div className="flex-1 min-w-0">
            <h3 className="font-bold text-base mb-1">{room.title}</h3>
            <p className="text-xs text-black/70 leading-snug">{a.summary}</p>
          </div>
        </div>
        {a.clutter_level && (
          <div className="flex items-center gap-2 mt-2 text-xs">
            <span className="font-bold">Kaosnivå:</span>
            <span className="px-2 py-0.5 border-[2px] border-black rounded-full font-bold"
              style={{ background: a.clutter_level === 'high' ? '#FFB3BA' : a.clutter_level === 'medium' ? '#FFE8A3' : '#B9FBC0' }}>
              {a.clutter_level === 'high' ? 'Hög' : a.clutter_level === 'medium' ? 'Medel' : 'Låg'}
            </span>
          </div>
        )}
      </div>

      {/* Zones */}
      {zones.length > 0 && (
        <div className="neo-card p-4 bg-[#FDF0D5]">
          <h3 className="font-bold mb-2 flex items-center gap-2"><Sparkles className="w-4 h-4" /> Zonförslag</h3>
          <div className="space-y-2">
            {zones.map((z, i) => (
              <div key={i} className="flex gap-2 items-start text-sm" data-testid="room-zone-item">
                <span className="mt-0.5">🧺</span>
                <div>
                  <span className="font-bold">{z.name}:</span>{' '}
                  <span className="text-black/70">{z.purpose}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Layout / furniture flow tips */}
      {layoutTips.length > 0 && (
        <div className="neo-card p-4 bg-[#E8F5FF]" data-testid="room-layout-section">
          <h3 className="font-bold mb-2 flex items-center gap-2">
            <Move className="w-4 h-4" /> Möblering & flöde
          </h3>
          <p className="text-[11px] text-black/60 mb-2 italic">
            Hur du kan flytta möbler för bättre ljus, fokus och naturliga gångvägar 🛋️
          </p>
          <div className="grid gap-2">
            {layoutTips.map((t, i) => (
              <div
                key={i}
                className="border-2 border-black rounded-xl p-2.5 bg-white flex gap-2 items-start"
                data-testid="room-layout-tip"
              >
                <span className="text-xl shrink-0 mt-0.5">{t.emoji || '🛋️'}</span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm leading-tight">{t.title}</h4>
                  <p className="text-xs text-black/70 leading-snug mt-0.5">{t.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Aesthetic / kawaii touches */}
      {aestheticTouches.length > 0 && (
        <div className="neo-card p-4 bg-gradient-to-br from-[#FFE8F5] to-[#F3E8FF]" data-testid="room-aesthetic-section">
          <h3 className="font-bold mb-2 flex items-center gap-2">
            <Sparkles className="w-4 h-4" /> Estetiska små touches
          </h3>
          <p className="text-[11px] text-black/60 mb-2 italic">
            Mjuka kawaii-tillägg som skapar lugn utan att skapa kaos 🌸
          </p>
          <div className="grid gap-2 sm:grid-cols-2">
            {aestheticTouches.map((t, i) => (
              <div
                key={i}
                className="border-2 border-black rounded-xl p-2.5 bg-white flex gap-2 items-start"
                data-testid="room-aesthetic-touch"
              >
                <span className="text-xl shrink-0 mt-0.5">{t.emoji || '🌸'}</span>
                <div className="flex-1 min-w-0">
                  <h4 className="font-bold text-sm leading-tight">{t.title}</h4>
                  <p className="text-xs text-black/70 leading-snug mt-0.5">{t.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Suggestions */}
      {sugg.length > 0 && (
        <div>
          <div className="flex items-center justify-between mb-2">
            <h3 className="font-bold flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" />
              Konkreta steg
            </h3>
            <span className="text-xs font-bold px-2 py-1 border-[2px] border-black rounded-full bg-white">
              {doneCount} / {total} klara
            </span>
          </div>
          {/* Progress bar */}
          <div className="mb-3 h-3 w-full border-[2px] border-black rounded-full overflow-hidden bg-white" data-testid="room-progress-bar">
            <div
              className="h-full transition-all duration-500"
              style={{
                width: `${pct}%`,
                background: pct === 100 ? 'linear-gradient(90deg,#B9FBC0,#C8B6FF,#FFC8DD)' : '#B9FBC0',
              }}
            />
          </div>
          <div className="grid gap-2">
            {sugg.map((s, i) => (
              <SuggestionCard
                key={i}
                s={s}
                done={completedSet.has(i)}
                busy={togglingIdx === i}
                onToggle={() => onToggleSuggestion(i, !completedSet.has(i))}
              />
            ))}
          </div>
          {pct === 100 && (
            <div className="mt-3 neo-card p-3 text-center bg-gradient-to-br from-[#B9FBC0] via-[#FFE8F0] to-[#C8B6FF] font-bold" data-testid="room-all-done-banner">
              🎉 Alla steg klara! Din kompis applåderar! 👏
            </div>
          )}
        </div>
      )}

      {/* Delete */}
      <button
        onClick={onDelete}
        className="neo-btn w-full py-2 bg-white text-red-600 flex items-center justify-center gap-2 text-sm"
        data-testid="room-delete-btn"
      >
        <Trash2 className="w-4 h-4" /> Ta bort rum
      </button>
    </div>
  );
}

export default function RoomAnalyzer({ onPointsChange } = {}) {
  const [rooms, setRooms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [analyzing, setAnalyzing] = useState(false);
  const [visualizing, setVisualizing] = useState(false);
  const [current, setCurrent] = useState(null); // full room object
  const [title, setTitle] = useState('');
  const [focus, setFocus] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [fileData, setFileData] = useState(null); // base64 data url
  const [togglingIdx, setTogglingIdx] = useState(null);
  const fileInputRef = useRef(null);
  const cameraInputRef = useRef(null);

  const fetchRooms = async () => {
    try {
      const { data } = await axios.get(`${API}/rooms`, { withCredentials: true });
      setRooms(data.rooms || []);
      window.dispatchEvent(new CustomEvent('rooms:updated'));
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchRooms(); }, []);

  const onPickFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      toast.error('Välj en bildfil (jpg/png)');
      return;
    }
    try {
      const dataUrl = await fileToCompressedBase64(file);
      setPreviewUrl(dataUrl);
      setFileData(dataUrl);
      if (!title) setTitle(file.name.replace(/\.[^.]+$/, '').slice(0, 40));
    } catch {
      toast.error('Kunde inte läsa bilden');
    }
  };

  const submitAnalyze = async () => {
    if (!fileData) {
      toast.error('Välj en bild först');
      return;
    }
    setAnalyzing(true);
    try {
      const { data } = await axios.post(
        `${API}/rooms/analyze`,
        { image_base64: fileData, title: title || 'Rum', focus: focus || null },
        { withCredentials: true, timeout: 90000 }
      );
      toast.success('✨ Analys klar!');
      // Fetch full room (with image) to display
      const { data: full } = await axios.get(`${API}/rooms/${data.id}`, { withCredentials: true });
      setCurrent(full);
      setPreviewUrl(null);
      setFileData(null);
      setTitle('');
      setFocus('');
      if (fileInputRef.current) fileInputRef.current.value = '';
      if (cameraInputRef.current) cameraInputRef.current.value = '';
      fetchRooms();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kunde inte analysera bilden');
    } finally {
      setAnalyzing(false);
    }
  };

  const onVisualize = async () => {
    if (!current) return;
    setVisualizing(true);
    try {
      const { data } = await axios.post(
        `${API}/rooms/${current.id}/visualize`,
        {},
        { withCredentials: true, timeout: 180000 }
      );
      setCurrent({ ...current, visualized_image: data.visualized_image });
      toast.success('🎨 Visualisering klar!');
      fetchRooms();
    } catch (e) {
      console.error(e);
      toast.error(e?.response?.data?.detail || 'Kunde inte skapa visualisering');
    } finally {
      setVisualizing(false);
    }
  };

  const onDelete = async () => {
    if (!current) return;
    if (!window.confirm('Ta bort detta rum?')) return;
    try {
      await axios.delete(`${API}/rooms/${current.id}`, { withCredentials: true });
      setCurrent(null);
      fetchRooms();
      toast.success('Borttaget');
    } catch {
      toast.error('Kunde inte ta bort');
    }
  };

  const openRoom = async (id) => {
    try {
      const { data } = await axios.get(`${API}/rooms/${id}`, { withCredentials: true });
      setCurrent(data);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch {
      toast.error('Kunde inte öppna rummet');
    }
  };

  const toggleSuggestion = async (idx, done) => {
    if (!current || togglingIdx !== null) return;
    setTogglingIdx(idx);
    // Optimistic update
    const prev = current.completed_suggestions || [];
    const optimistic = new Set(prev);
    if (done) optimistic.add(idx); else optimistic.delete(idx);
    setCurrent({ ...current, completed_suggestions: Array.from(optimistic).sort() });
    try {
      const { data } = await axios.patch(
        `${API}/rooms/${current.id}/suggestions/${idx}`,
        { done },
        { withCredentials: true }
      );
      setCurrent((c) => c ? { ...c, completed_suggestions: data.completed_suggestions } : c);
      if (data.bonus && data.bonus > 0) {
        toast.success(`🎉 Alla steg klara! +${data.bonus} bonuspoäng!`);
      } else if (data.points_delta > 0) {
        toast.success(`+${data.points_delta} poäng ✨`);
      }
      if (data.points_delta !== 0 && onPointsChange) onPointsChange();
      fetchRooms();
    } catch (e) {
      // Revert
      setCurrent((c) => c ? { ...c, completed_suggestions: prev } : c);
      toast.error(e?.response?.data?.detail || 'Kunde inte uppdatera');
    } finally {
      setTogglingIdx(null);
    }
  };

  return (
    <div className="space-y-6" data-testid="room-analyzer">
      <header className="neo-card p-4 bg-gradient-to-br from-[#FFC8DD] via-[#FFE8F0] to-[#C8B6FF]">
        <h2 className="font-bold text-lg flex items-center gap-2">
          <Wand2 className="w-5 h-5" /> Mitt Rum — möblering, flöde & kawaii estetik
        </h2>
        <p className="text-xs text-black/70 mt-1">
          Ta ett kort på ett rum, en låda eller hylla. Din kawaii-kompis ger en helhetsanalys:
          möblering & flöde 🛋️ + ADHD-vänliga zoner 🧺 + mjuka estetiska touches 🌸
        </p>
      </header>

      {current ? (
        <div className="space-y-3">
          <button
            onClick={() => setCurrent(null)}
            className="neo-btn px-3 py-1.5 text-sm bg-white"
            data-testid="room-back-btn"
          >
            ← Tillbaka
          </button>
          <AnalysisView
            room={current}
            onVisualize={onVisualize}
            visualizing={visualizing}
            onDelete={onDelete}
            onToggleSuggestion={toggleSuggestion}
            togglingIdx={togglingIdx}
          />
        </div>
      ) : (
        <>
          {/* Upload form */}
          <div className="neo-card p-4 bg-white space-y-3" data-testid="room-upload-card">
            <div className="flex items-center gap-2">
              <Camera className="w-5 h-5" />
              <h3 className="font-bold">Nytt kort</h3>
            </div>

            {previewUrl ? (
              <div className="relative">
                <img
                  src={previewUrl}
                  alt="förhandsvisning"
                  className="w-full max-h-80 object-cover border-[3px] border-black rounded-2xl"
                  data-testid="room-preview-img"
                />
                <button
                  onClick={() => {
                    setPreviewUrl(null);
                    setFileData(null);
                    if (fileInputRef.current) fileInputRef.current.value = '';
                    if (cameraInputRef.current) cameraInputRef.current.value = '';
                  }}
                  className="absolute top-2 right-2 neo-btn p-1.5 bg-white"
                  aria-label="Ta bort bild"
                  data-testid="room-clear-image-btn"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <button
                  onClick={() => fileInputRef.current?.click()}
                  className="py-6 border-[3px] border-dashed border-black rounded-2xl bg-[#FFF7E0] hover:bg-[#FFE8A3] transition flex flex-col items-center gap-1 font-bold text-sm"
                  data-testid="room-pick-image-btn"
                >
                  <ImageIcon className="w-6 h-6" />
                  <span>Från album</span>
                </button>
                <button
                  onClick={() => cameraInputRef.current?.click()}
                  className="py-6 border-[3px] border-dashed border-black rounded-2xl bg-[#E8F5FF] hover:bg-[#A2D2FF] transition flex flex-col items-center gap-1 font-bold text-sm"
                  data-testid="room-camera-btn"
                >
                  <Camera className="w-6 h-6" />
                  <span>Ta kort nu</span>
                </button>
              </div>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={onPickFile}
              className="hidden"
              data-testid="room-file-input"
            />
            <input
              ref={cameraInputRef}
              type="file"
              accept="image/*"
              capture="environment"
              onChange={onPickFile}
              className="hidden"
              data-testid="room-camera-input"
            />

            <div>
              <label className="text-xs font-bold block mb-1">Vad är det här? (valfritt)</label>
              <input
                type="text"
                placeholder="T.ex. Badrumslåda, Sovrum, Köksbänk"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="neo-input w-full"
                maxLength={60}
                data-testid="room-title-input"
              />
            </div>

            <div>
              <label className="text-xs font-bold block mb-1">Önskemål (valfritt)</label>
              <input
                type="text"
                placeholder="T.ex. Allt måste ha sin plats, mer lugn"
                value={focus}
                onChange={(e) => setFocus(e.target.value)}
                className="neo-input w-full"
                maxLength={160}
                data-testid="room-focus-input"
              />
            </div>

            <button
              onClick={submitAnalyze}
              disabled={analyzing || !fileData}
              className="neo-btn w-full py-3 bg-gradient-to-br from-[#B9FBC0] to-[#A2D2FF] font-bold flex items-center justify-center gap-2 disabled:opacity-60"
              data-testid="room-analyze-btn"
            >
              {analyzing ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  Analyserar... (ca 10-20 sek)
                </>
              ) : (
                <>
                  <Sparkles className="w-5 h-5" />
                  Analysera rummet
                </>
              )}
            </button>
          </div>

          {/* History */}
          <div>
            <h3 className="font-bold mb-2 flex items-center gap-2">
              <HistoryIcon className="w-4 h-4" /> Mina rum ({rooms.length})
            </h3>
            {loading ? (
              <div className="text-center py-6 text-sm text-black/60">Laddar...</div>
            ) : rooms.length === 0 ? (
              <div className="neo-card p-6 bg-white text-center text-sm text-black/60">
                Inga rum sparade ännu. Ta ditt första kort ovan! 📸
              </div>
            ) : (
              <div className="grid gap-3 sm:grid-cols-2">
                {rooms.map((r) => {
                  const total = r.analysis?.suggestions?.length || 0;
                  const done = (r.completed_suggestions || []).length;
                  const pct = total ? Math.round((done / total) * 100) : 0;
                  return (
                    <button
                      key={r.id}
                      onClick={() => openRoom(r.id)}
                      className="neo-card p-3 bg-white text-left hover:bg-[#FDF0D5] transition"
                      data-testid="room-list-item"
                    >
                      <div className="flex items-start justify-between gap-2 mb-1">
                        <h4 className="font-bold text-sm truncate">{r.title}</h4>
                        {r.has_visualization && (
                          <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-[2px] border-black bg-[#C8B6FF] shrink-0">
                            ✨ Visualiserad
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-black/70 line-clamp-2 mb-1">
                        {r.analysis?.summary || '—'}
                      </p>
                      {total > 0 && (
                        <div className="mt-1 mb-1 h-2 w-full border-[2px] border-black rounded-full overflow-hidden bg-white">
                          <div
                            className="h-full"
                            style={{
                              width: `${pct}%`,
                              background: pct === 100 ? 'linear-gradient(90deg,#B9FBC0,#C8B6FF,#FFC8DD)' : '#B9FBC0',
                            }}
                          />
                        </div>
                      )}
                      <div className="flex items-center justify-between text-[10px] text-black/60">
                        <span>
                          Vibe: <span className="font-bold">{r.analysis?.vibe_score ?? '–'}/10</span>
                        </span>
                        <span>{done} / {total} klara</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
